// Display labels for the CRM. Kept in sync with the option lists exported by
// backend/models/Crm.js -- the API also serves them at /api/crm/meta.

export const STAGE_FLOW = ['planned', 'contacted', 'quoting', 'committed', 'ordered', 'delivered', 'lost', 'skipped'];

export const STAGE_LABELS = {
  planned: 'Planned',
  contacted: 'Contacted',
  quoting: 'Quoting',
  committed: 'Committed',
  ordered: 'Ordered',
  delivered: 'Delivered',
  lost: 'Lost',
  skipped: 'Skipped',
};

export const ORG_TYPE_LABELS = {
  isd: 'ISD',
  private_school: 'Private School',
  youth_league: 'Youth League',
  club: 'Club',
  rec_department: 'Rec Department',
  booster: 'Booster Club',
  college: 'College',
  other: 'Other',
};

export const ORG_STATUS_LABELS = {
  prospect: 'Prospect',
  active: 'Active',
  lapsed: 'Lapsed',
  inactive: 'Inactive',
};

export const BUDGET_MODEL_LABELS = {
  ad_controlled: 'AD controls the budget',
  coach_controlled: 'Each coach has their own',
  mixed: 'Mixed',
  unknown: 'Unknown',
};

export const ROLE_LABELS = {
  athletic_director: 'Athletic Director',
  head_coach: 'Head Coach',
  assistant_coach: 'Assistant Coach',
  booster_president: 'Booster President',
  team_parent: 'Team Parent',
  business_office: 'Business Office',
  principal: 'Principal',
  other: 'Other',
};

export const CHANNEL_LABELS = {
  text: 'Text',
  email: 'Email',
  phone: 'Phone call',
  paper: 'Paper',
  unknown: 'Unknown',
};

export const LEVEL_LABELS = {
  varsity: 'Varsity',
  jv: 'JV',
  freshman: 'Freshman',
  middle_school: 'Middle School',
  youth: 'Youth',
  open: 'Open',
};

export const GENDER_LABELS = { boys: 'Boys', girls: 'Girls', coed: 'Coed' };

export const FUNDING_LABELS = {
  school_funded: 'School funded',
  parent_funded: 'Parent funded',
  booster: 'Booster funded',
  mixed: 'Mixed',
  unknown: 'Unknown',
};

export const PAYMENT_LABELS = {
  unbilled: 'Unbilled',
  invoiced: 'Invoiced',
  partial: 'Partial',
  paid: 'Paid',
};

export const LOST_REASON_LABELS = {
  price: 'Price',
  timing: 'Timing',
  relationship: 'Relationship',
  district_contract: 'District contract',
  no_budget: 'No budget',
  other: 'Other',
};

export const ACTIVITY_LABELS = {
  call: 'Call',
  text: 'Text',
  email: 'Email',
  visit: 'Visit',
  meeting: 'Meeting',
  quote_sent: 'Quote sent',
  note: 'Note',
};

export const ACTIVITY_ICONS = {
  call: '📞',
  text: '💬',
  email: '✉️',
  visit: '🚗',
  meeting: '🤝',
  quote_sent: '📄',
  note: '📝',
};

export const money = (value) => {
  const n = Number(value || 0);
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
};

export const shortDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export const daysUntil = (value) => {
  if (!value) return null;
  const target = new Date(value);
  if (Number.isNaN(target.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);
  return Math.round((target - today) / 86400000);
};

// Program display name: "Football — Varsity Boys"
export const programName = (program) => {
  if (!program) return '';
  const level = LEVEL_LABELS[program.level] || program.level;
  const gender = program.gender === 'coed' ? '' : ` ${GENDER_LABELS[program.gender] || ''}`;
  return `${program.sport} — ${level}${gender}`.trim();
};

export const contactName = (row, prefix = 'contact') => {
  const first = row?.[`${prefix}_first_name`];
  const last = row?.[`${prefix}_last_name`];
  if (!first && !last) return null;
  return [first, last].filter(Boolean).join(' ');
};

// Team colors ride on the record as CSS custom properties, so a school's identity
// shows up wherever it's listed. Returns null when nothing is on file.
export const teamColorStyle = (row) => {
  if (!row?.color_primary && !row?.color_secondary) return null;
  const style = {};
  if (row.color_primary) style['--team-1'] = row.color_primary;
  if (row.color_secondary) style['--team-2'] = row.color_secondary;
  return style;
};
